import React, { useEffect, useRef } from "react";
import { micLevel, speakingLevel } from "../agent/voice";

// Listening reads cool, the copilot's own voice reads warm: the state is
// legible by colour before anyone reads the word under the orb.
const PALETTE = {
  listening: [
    [57, 83, 251],
    [43, 196, 200],
    [124, 92, 255]
  ],
  speaking: [
    [124, 92, 255],
    [255, 122, 89],
    [236, 72, 153]
  ],
  thinking: [
    [120, 132, 165],
    [90, 104, 140],
    [150, 160, 190]
  ]
};

/**
 * Conversation mode. One living object in the middle of the composer area:
 * three gradient lobes orbiting a shared centre, their radius driven by
 * whoever is talking — the user's microphone while listening, the copilot's
 * own audio while it speaks.
 */
const VoiceConversationOverlay = ({ state, transcript, onEnd }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Was 168. The card took over the thread; everything below is expressed
    // as the same fraction of `size` it was at 168, so the orb is the exact
    // same drawing, just smaller.
    const size = 104;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);

    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let t = 0;
    let amp = 0;

    const draw = () => {
      const mode = stateRef.current === "speaking" ? "speaking" : stateRef.current === "thinking" ? "thinking" : "listening";
      const raw = mode === "speaking" ? speakingLevel() : mode === "listening" ? micLevel() : 0;
      // -1 means no analyser: breathe on our own clock rather than freeze.
      const target = raw >= 0 ? raw : 0.35 + Math.sin(t / 9) * 0.25;
      amp += (target - amp) * 0.18;

      ctx.clearRect(0, 0, size, size);
      const cx = size / 2;
      const cy = size / 2;
      const base = size * (30 / 168) + amp * size * (22 / 168);
      ctx.globalCompositeOperation = "lighter";

      PALETTE[mode].forEach(([r, g, b], i) => {
        const angle = t / (mode === "speaking" ? 22 : 34) + (i * Math.PI * 2) / 3;
        const drift = size * (12 / 168) + amp * size * (16 / 168);
        const x = cx + Math.cos(angle) * drift;
        const y = cy + Math.sin(angle * 1.2) * drift;
        const radius = base + Math.sin(t / 11 + i) * size * (6 / 168);
        const grad = ctx.createRadialGradient(x, y, 0, x, y, radius);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.85)`);
        grad.addColorStop(0.55, `rgba(${r},${g},${b},0.28)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Containment ring: the orb is an object, not a smudge.
      ctx.globalCompositeOperation = "source-over";
      ctx.beginPath();
      ctx.arc(cx, cy, size * (62 / 168) + amp * size * (8 / 168), 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(17,24,39,${0.06 + amp * 0.08})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      t += reduced ? 0.25 : 1;
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const label =
    state === "speaking" ? "Copilot is speaking" : state === "thinking" ? "Working on it" : "Listening";

  return (
    <div className={`copilotConvo copilotConvo--${state}`} role="dialog" aria-label="Voice conversation">
      <canvas className="copilotConvo__orb" ref={canvasRef} aria-hidden="true" />
      <p className="copilotConvo__state">{label}</p>
      <p className="copilotConvo__transcript">{transcript || "Just talk — interrupt any time."}</p>
      <button type="button" className="copilotConvo__end" onClick={onEnd}>
        End conversation
      </button>
    </div>
  );
};

export default VoiceConversationOverlay;
